import React from 'react';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { StudioActionError } from '../../studio/StudioActionError';
import type { StudioActionFeedback } from '../../studio/studioActionErrorText';
import { ThemeSwatches } from '../page/ThemeSwatches';
import { profileToolMessages as t } from './profileToolMessages';
import { withBrandWord } from '../../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileToolRailProps {
  preview: React.ReactNode;
  /** The switches under the preview that change the file ("Same picture for both", "Use it as my Music24 photo too"). */
  switches?: React.ReactNode;
  guideNote: string;
  meta: readonly string[];
  colours: ThemeColours;
  /** The "Colours" line; null for a photo kind (no colours are added). */
  coloursLine: string;
  makeLabel: string;
  canMake: boolean;
  isMaking: boolean;
  /** Why "Make it" is off ("Pick a photo first."), or null. */
  blockedReason: string | null;
  feedback: StudioActionFeedback | null;
  /** A note after a make that the server took. */
  madeNote: string | null;
  onMake: () => void;
  /** Download, Upload to Spotify, I set it. */
  actions: React.ReactNode;
}

/**
 * The rail of a Profile tool (design b1–b4): the preview, the guide note, the file facts, the
 * colours, "This costs: Free", Make it, then Download, Upload to Spotify or I set it. The action
 * error stays next to Make it (the studioActionError rule).
 */
const ProfileToolRailComponent: React.FC<ProfileToolRailProps> = (props) => {
  const { preview, switches, guideNote, meta, colours, coloursLine, makeLabel, canMake, isMaking } = props;
  const { blockedReason, feedback, madeNote, onMake, actions } = props;
  const intl = useIntl();
  return (
    <aside className={styles.rail} data-testid="profile-tool-rail">
      <div className={styles.railHead}>
        <b>{intl.formatMessage(t.previewTitle)}</b>
      </div>
      {preview}
      {switches !== undefined && <div className={styles.toggles}>{switches}</div>}
      <p className={styles.note}>{withBrandWord(guideNote)}</p>
      <div className={styles.metaChips}>
        {meta.map((chip) => (
          <span key={chip} className={styles.metaChip}>
            {chip}
          </span>
        ))}
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center small">
        <span className={styles.eyebrow}>{intl.formatMessage(t.colours)}</span>
        <ThemeSwatches colours={colours} />
        <span className={styles.muted}>{coloursLine}</span>
      </div>
      <div className={styles.cost}>
        <span>{intl.formatMessage(t.costLabel)}</span>
        <b>{intl.formatMessage(t.costFree)}</b>
      </div>
      {blockedReason !== null && <span className={styles.meta}>{blockedReason}</span>}
      <SMLButton variant="primary" className={styles.makeButton} disabled={!canMake || isMaking} onClick={onMake}>
        {isMaking ? intl.formatMessage(t.makingNow) : makeLabel}
      </SMLButton>
      <StudioActionError feedback={feedback} />
      {madeNote !== null && (
        <span className={styles.meta} role="status">
          {madeNote}
        </span>
      )}
      <div className={styles.row}>{actions}</div>
    </aside>
  );
};

export const ProfileToolRail = TypedMemo(ProfileToolRailComponent);
